import { useEffect, useRef, useState } from 'react'

interface Props {
  cameraName: string
  whepUrl: string | null
}

type ConnectionStatus = 'loading' | 'connected' | 'failed'

const CONNECTION_STATUS_LABEL: Record<ConnectionStatus, string> = {
  loading: '연결 중',
  connected: '연결됨',
  failed: '연결 실패',
}

export default function WhepPlayer({ cameraName, whepUrl }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    whepUrl ? 'loading' : 'failed',
  )
  const [retryGeneration, setRetryGeneration] = useState(0)

  useEffect(() => {
    if (!whepUrl || !videoRef.current) {
      setConnectionStatus('failed')
      setError(whepUrl ? null : 'WHEP 재생 주소가 없습니다.')
      return
    }

    setConnectionStatus('loading')
    setError(null)
    let cancelled = false
    const connection = new RTCPeerConnection()
    const video = videoRef.current
    const endpoint = whepUrl

    async function connect() {
      try {
        const videoTransceiver = connection.addTransceiver('video', { direction: 'recvonly' })
        const h264Codecs = RTCRtpReceiver.getCapabilities('video')?.codecs
          .filter((codec) => codec.mimeType.toLowerCase() === 'video/h264')
        if (h264Codecs?.length) videoTransceiver.setCodecPreferences(h264Codecs)
        connection.addTransceiver('audio', { direction: 'recvonly' })
        connection.ontrack = (event) => {
          const stream = event.streams[0] ?? new MediaStream([event.track])
          video.srcObject = stream
          void video.play().catch(() => {})
        }
        connection.oniceconnectionstatechange = () => {
          if (cancelled) return
          switch (connection.iceConnectionState) {
            case 'connected':
            case 'completed':
              setConnectionStatus('connected')
              setError(null)
              break
            case 'failed':
              setConnectionStatus('failed')
              setError('WebRTC ICE 연결에 실패했습니다.')
              break
            case 'disconnected':
              setConnectionStatus('failed')
              setError('WebRTC ICE 연결이 끊어졌습니다.')
              break
            case 'closed':
              setConnectionStatus('failed')
              setError('WebRTC 연결이 종료되었습니다.')
              break
          }
        }
        const offer = await connection.createOffer()
        await connection.setLocalDescription(offer)
        await waitForIceGathering(connection)
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/sdp' },
          body: connection.localDescription?.sdp ?? offer.sdp,
        })
        if (!response.ok) throw new Error(`WHEP POST 실패 (HTTP ${response.status})`)
        const answer = await response.text()
        if (!cancelled) {
          await connection.setRemoteDescription({ type: 'answer', sdp: answer })
          setError(null)
        }
      } catch (caught: unknown) {
        if (!cancelled) {
          setConnectionStatus('failed')
          setError((caught as Error).message || '영상 연결에 실패했습니다.')
        }
      }
    }

    void connect()
    return () => {
      cancelled = true
      connection.ontrack = null
      connection.oniceconnectionstatechange = null
      connection.close()
      const attachedStream = video.srcObject as MediaStream | null
      attachedStream?.getTracks?.().forEach((track) => track.stop())
      video.srcObject = null
    }
  }, [retryGeneration, whepUrl])

  function reconnect() {
    if (!whepUrl) return
    setConnectionStatus('loading')
    setError(null)
    setRetryGeneration((current) => current + 1)
  }

  return (
    <article className="manual-camera-card">
      <div className="manual-camera-heading">
        <div className="manual-camera-title">{cameraName}</div>
        <span
          className={`manual-camera-status ${connectionStatus}`}
          role="status"
          aria-live="polite"
        >
          {CONNECTION_STATUS_LABEL[connectionStatus]}
        </span>
      </div>
      <video ref={videoRef} controls muted playsInline />
      {error && (
        <div className="manual-camera-error" role="alert">
          <span>{error}</span>
          <button
            type="button"
            className="manual-camera-retry"
            onClick={reconnect}
            disabled={!whepUrl}
          >
            다시 연결
          </button>
        </div>
      )}
    </article>
  )
}

function waitForIceGathering(connection: RTCPeerConnection): Promise<void> {
  if (connection.iceGatheringState === 'complete') return Promise.resolve()
  return new Promise((resolve) => {
    function finish() {
      window.clearTimeout(timeout)
      connection.removeEventListener('icegatheringstatechange', handler)
      resolve()
    }
    const handler = () => {
      if (connection.iceGatheringState !== 'complete') return
      finish()
    }
    const timeout = window.setTimeout(finish, 3000)
    connection.addEventListener('icegatheringstatechange', handler)
  })
}
